import * as React from 'react';
import { CopyOutlined, MailOutlined } from '@ant-design/icons';
import { css, useTheme } from '@emotion/react';
import type { SerializedStyles, Theme } from '@emotion/react';
import { BrandIcon, Draft, IconButton, Inline, Stack } from '@resi-media/resi-ui';
import copy from 'copy-to-clipboard';
import { useLocation } from 'react-router';
import { GOOGLE_EVENTS } from '@sites/constants/google-events';
import { facebookAppId } from '@studio/constants/api-constants';
import { SOCIAL_PLATFORMS } from '@studio/constants/shared';
import usePrefix from '@studio/hooks/use-prefix/use-prefix';

const S = {
  iconColor: (theme: Theme): SerializedStyles => css`
    color: ${theme.palette.text.title};
    background-color: transparent;

    :hover:not(:disabled, &[data-${theme.prefix}-button-base--disabled]) {
      background-color: ${theme.palette.activeMode === 'dark' ? theme.palette.grey[1000] : theme.palette.grey[200]};
    }

    :active:not(:disabled, &[data-${theme.prefix}-button-base--disabled]),
        &[aria-pressed='true']:not(:disabled, &[data-${theme.prefix}-button-base--disabled]) {
      color: ${theme.palette.text.title};
      background-color: ${theme.palette.disabled.background};
    }

    :disabled {
      color: ${theme.palette.disabled.foreground};
      background-color: transparent;
    }
  `,
  iconFocus: (theme: Theme): SerializedStyles => css`
    :focus:not(:disabled) {
      box-shadow: 0 0 0 2px ${theme.palette.info.main};
    }
  `,
};

export type ShareModalState = {
  shareUrl: string;
  videoTitle: string;
};

const ShareModal = () => {
  const siteTheme = useTheme();
  const [tooltipVisibility, setTooltipVisibility] = React.useState(false);
  const { onCloseReset, shareUrl, videoTitle } = Draft.ModalContext.useModal<ShareModalState>();
  const { commonT, prefixNS } = usePrefix('components:', 'shareModal');
  const { pathname, search } = useLocation();
  // When testing this on localhost, facebook will not permit the share dialog to open
  const pageUrl = shareUrl || `${window.location.origin}${pathname}${search || ''}`;

  const handleCopyClick = () => {
    gtag('event', GOOGLE_EVENTS.COPY_BUTTON_CLICK, { media_asset_name: videoTitle, type: 'link' });
    copy(pageUrl);
    setTooltipVisibility(true);
    setTimeout(() => {
      setTooltipVisibility(false);
    }, 800);
  };

  return (
    <React.Fragment>
      <Draft.ModalHeader header={prefixNS('header')} onClose={onCloseReset} showBorder />
      <Draft.ModalBody
        css={css`
          background-color: transparent;
        `}
      >
        <Stack gap="m">
          <Draft.CardIconGroup defaultIndex={-1} useResponsive={false}>
            <Draft.CardIconButton
              css={(theme) => [S.iconColor(theme), S.iconFocus(theme)]}
              dataTestId="share-modal__card--facebook"
              icon={
                <BrandIcon
                  colorVariant={siteTheme.palette.activeMode === 'dark' ? 'white' : 'default'}
                  dataTestId="share-modal__icon-fb"
                  variant="fb"
                />
              }
              id="a"
              label={prefixNS('fields.shareOptions.facebook')}
              onClick={() => {
                gtag('event', GOOGLE_EVENTS.COPY_BUTTON_CLICK, {
                  media_asset_name: videoTitle,
                  type: SOCIAL_PLATFORMS.FACEBOOK,
                });
                window.open(
                  `https://www.facebook.com/dialog/share?app_id=${facebookAppId}&display=popup&href=${pageUrl}`,
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            />
            <Draft.CardIconButton
              css={(theme) => [S.iconColor(theme), S.iconFocus(theme)]}
              dataTestId="share-modal__card--email"
              href={`mailto:?subject=${encodeURIComponent(
                prefixNS('subject', { title: videoTitle })
              )}&body=${encodeURIComponent(pageUrl)}`}
              icon={<MailOutlined />}
              id="b"
              label={prefixNS('fields.shareOptions.email')}
              onClick={() =>
                gtag('event', GOOGLE_EVENTS.COPY_BUTTON_CLICK, { media_asset_name: videoTitle, type: 'mail' })
              }
            />
          </Draft.CardIconGroup>
          <Draft.FormField fieldLabel={prefixNS('fields.link.label')}>
            <Draft.Tooltip
              content={commonT('copied')}
              dataTestId="tooltip"
              popoverProps={{ isOpen: tooltipVisibility }}
            >
              <Inline alignItems="center" gap="m">
                <Draft.TextInput
                  as="textarea"
                  css={(theme) => css`
                    max-height: calc(
                      (4 * (${theme.typography.body2.lineHeight} * ${theme.typography.body2.fontSize})) +
                        ${theme.spacing.m}
                    );
                  `}
                  dataTestId="share-modal__input--copy"
                  defaultValue={pageUrl}
                  onClick={handleCopyClick}
                  readOnly
                  resize="vertical"
                  rows={2}
                />
                <IconButton
                  colorVariant="neutral"
                  dataTestId="share-modal__button--copy"
                  label={commonT('copy')}
                  onClick={handleCopyClick}
                  sizeVariant="m"
                >
                  <CopyOutlined />
                </IconButton>
              </Inline>
            </Draft.Tooltip>
          </Draft.FormField>
        </Stack>
      </Draft.ModalBody>
    </React.Fragment>
  );
};

export { ShareModal };
